import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-image-lightbox/style.css";
import { Button, Col, FormGroup, Row } from "reactstrap";
import PageBody from "../../../components/PageBody";
import { PChart, Dataset } from "pchart";
import { BMI_FEMALE_5_19, BMI_MALE_5_19, HEIGHT_FEMALE_5_19, HEIGHT_MALE_5_19, LESS_THAN_60_MONTHS, WEIGHT_FEMALE_5_10, WEIGHT_MALE_5_10 } 
from "../../../constants/bmi";
import moment from "moment";
import { LMSDataset, Patient, Theme } from "pchart/dist/types";
import ReactApexChart from "react-apexcharts";
import { cloneDeep } from "lodash";
import { useHistory } from "react-router-dom";
import CustomerChartPrint from "./CustomerChartPrint";
import { useReactToPrint } from "react-to-print";

const theme = {
  backdropFill: "#B2EBF2",
  gridColor: "#00ACC1",
  areaColor: "rgba(0,172,193,.4)",
};

export const CustomerChartHeight1 = (props: any) => {
  const { diff, patient } = props;
  const percentiles = [50, 75, 95];
  const percentileValues: LMSDataset = {
    title: "Height for age, girls, from 0 to 13 weeks",
    titleX: "Months",
    unitX: "month",
    titleY: "Height (cm)",
    unitY: "cm",
    dataType: "height",
    data: [[0, 1, 49.1477, 0.0379, 0.0379]],
  };
  if (diff?.months <= 60 && patient?.sex === "male") {
    percentileValues.title = "Height for age, boys, from 0 to 12 months";
    percentileValues.titleX = "Months";
    percentileValues.unitX = "month";
    percentileValues.titleY = "Height (cm)";
    percentileValues.unitY = "cm";
    percentileValues.dataType = "height";
    percentileValues.data = LESS_THAN_60_MONTHS?.male?.height?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, [item?.month, item["-2sd"], item["m"], item["2sd"]]];
        return prev;
      },
      []
    );
  } else if (diff?.months <= 60 && patient?.sex === "female") {
    percentileValues.title = "Height for age, girls, from 0 to 13 weeks";
    percentileValues.titleX = "Months";
    percentileValues.unitX = "month";
    percentileValues.titleY = "Height (cm)";
    percentileValues.unitY = "cm";
    percentileValues.dataType = "height";
    percentileValues.data = LESS_THAN_60_MONTHS?.female?.height?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, [item?.month, item["-2sd"], item["m"], item["2sd"]]];
        return prev;
      },
      []
    );
  }
  const dataset = new Dataset(percentileValues as LMSDataset, percentiles);
  return (
    <PChart
      width={720}
      height={600}
      theme={theme}
      showtitle
      showlabels
      showlines
      dataset={dataset}
      patients={patient}
    />
  );
};

const getBmi = (data: any) => {
  const weight = Number(data?.weight) || 0;
  const height = Number(data?.height) / 100 || 1;
  const bmiNumber = weight / (height * height)
  return Math.round(bmiNumber * 100) / 100
}

const COLORS = {
  '1sd': '#ffdd2c',
  '2sd': '#ff0000',
  '3sd': '#000000',
  'm': '#4bff2c',
  'p': '#59287A',
}

const OPTIONS: any = {
  chart: {
    height: 800,
    type: "line",
    zoom: {

      enabled: false,
    },
  },
  annotations: {
    points: []
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    show: true,
    curve: 'smooth',
    lineCap: 'butt',
    colors: undefined,
    width: 1.5,
    dashArray: 0,
  },
  title: {
    text: "",
    align: "left",
    enabled: false,
  },
  color: "#ff0000",
  grid: {
    show: true,
    borderColor: 'black',
    strokeDashArray: 0,
    position: 'back',
    xaxis: {
      lines: {
        show: true,
        opacity: 1,
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    },
    padding: {
      top: 10,
      right: 10,
      bottom: 10,
      left: 10
    },
    row: {
      colors: [],
      opacity: 1,
    },
    column: {
      colors: [],
      opacity: 1,
    },
  },
  xaxis: {
    categories: [],
    position: 'bottom',
    labels: {
      show: true,
      rotate: -45,
      rotateAlways: false,
      hideOverlappingLabels: true,
      showDuplicates: false,
      trim: false,
      minHeight: undefined,
      style: {
        colors: [],
        fontSize: '6px',
        lineHeight: '18px',
        marginBottom: 10
      },
      offsetX: 0,
      offsetY: 0,
      format: undefined,
      formatter: undefined,
    },
    title: {
      text: 'Tuổi'
    }
  },
  yaxis: {
    title: {
      text: 'Chiều cao(cm)'
    },
    min: 0
  },
};

export const CustomerChartHeight = (props: any) => {
  const { diff, patient, customer, type, width } = props;

  let examinations = customer?.examinations
  const curentPointLockup: any = []
  const birthDate = moment(customer?.birthday);
  examinations?.map((item: any) => {
    const month = moment(item?.day).diff(birthDate, "months");
    if (Number(item?.height)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      height: Number(item?.height)
    }
    if (Number(item?.weight)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      weight: Number(item?.weight),
    }
    if (Number(item?.height) && Number(item?.weight)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      bmi: getBmi(item),
    }
  })
  const options: any = cloneDeep(OPTIONS)
  let series = [];
  if (diff?.months <= 60 && patient?.sex === "male") {
    options.colors = [COLORS?.["2sd"], COLORS?.["m"], COLORS?.["2sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ chiều cao so với tuổi của bé trai từ 0 đến 60 tháng"
    options.xaxis.title.text = "Tháng"
    options.xaxis.categories = LESS_THAN_60_MONTHS?.male?.height?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.month];
        return prev;
      },
      []
    );
    let i: number = 0
    series = LESS_THAN_60_MONTHS?.male?.height?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-2sd"])
        prev[1].data.push(item["m"])
        prev[2].data.push(item["2sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[3].data.push(curentPointLockup[Number(item?.month)]?.height)
          if (curentPointLockup[Number(item?.month)]?.height) {
            i++;
            options.annotations.points.push({
              x: Number(item?.month),
              y: curentPointLockup[Number(item?.month)]?.height,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },

              },
            })
          }
        } else {
          prev[3].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "m",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.months <= 60 && patient?.sex === "female") {
    options.colors = [COLORS?.["2sd"], COLORS?.["m"], COLORS?.["2sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ chiều cao so với tuổi của bé gái từ 0 đến 60 tháng"
    options.xaxis.title.text = "Tháng"
    options.xaxis.categories = LESS_THAN_60_MONTHS?.female?.height?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.month];
        return prev;
      },
      []
    );
    let i: number = 0
    series = LESS_THAN_60_MONTHS?.female?.height?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-2sd"])
        prev[1].data.push(item["m"])
        prev[2].data.push(item["2sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[3].data.push(curentPointLockup[Number(item?.month)]?.height)
          if (curentPointLockup[Number(item?.month)]?.height) {
            i++;
            options.annotations.points.push({
              x: Number(item?.month),
              y: curentPointLockup[Number(item?.month)]?.height,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
                chart: {
                  type: 'line', // Specify the chart type as "line"
                },
              },
            })
          }
        } else {
          prev[3].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "m",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.years <= 19 && patient?.sex === "male") {
    options.colors = [COLORS?.["3sd"], COLORS?.["2sd"], COLORS?.["1sd"], COLORS?.["m"], COLORS?.["1sd"], COLORS?.["2sd"], COLORS?.["3sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ chiều cao so với tuổi của bé trai từ 5 đến 19 tuổi"
    options.xaxis.title.text = "Năm : Tháng"
    options.xaxis.categories = HEIGHT_MALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.name];
        return prev;
      },
      []
    );
    let i: number = 0
    series = HEIGHT_MALE_5_19?.reduce(
      (prev: any, item: any, index: any) => {
        prev[0].data.push(item["-3sd"])
        prev[1].data.push(item["-2sd"])
        prev[2].data.push(item["-1sd"])
        prev[3].data.push(item["Median"])
        prev[4].data.push(item["1sd"])
        prev[5].data.push(item["2sd"])
        prev[6].data.push(item["3sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[7].data.push(curentPointLockup[Number(item?.month)]?.height)
          if (curentPointLockup[Number(item?.month)]?.height) {
            i++;
            options.annotations.points.push({
              x: item?.name,
              y: curentPointLockup[Number(item?.month)]?.height,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                text: "1",
                radius: 2,
                textAnchor: "1",
                cssClass: "apexcharts-custom-class",
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[7].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-3sd",
          data: [],
        },
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "-1sd",
          data: [],
        },
        {
          name: "Median",
          data: [],
        },
        {
          name: "1sd",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "3sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.years <= 19 && patient?.sex === "female") {
    options.colors = [COLORS?.["3sd"], COLORS?.["2sd"], COLORS?.["1sd"], COLORS?.["m"], COLORS?.["1sd"], COLORS?.["2sd"], COLORS?.["3sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ chiều cao so với tuổi của bé gái từ 5 đến 19 tuổi"
    options.xaxis.title.text = "Năm : Tháng"
    options.xaxis.categories = HEIGHT_FEMALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.name];
        return prev;
      },
      []
    );
    let i: number = 0
    series = HEIGHT_FEMALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-3sd"])
        prev[1].data.push(item["-2sd"])
        prev[2].data.push(item["-1sd"])
        prev[3].data.push(item["Median"])
        prev[4].data.push(item["1sd"])
        prev[5].data.push(item["2sd"])
        prev[6].data.push(item["3sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[7].data.push(curentPointLockup[Number(item?.month)]?.height)
          if (curentPointLockup[Number(item?.month)]?.height) {
            i++;
            options.annotations.points.push({
              x: item?.name,
              y: curentPointLockup[Number(item?.month)]?.height,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[7].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-3sd",
          data: [],
        },
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "-1sd",
          data: [],
        },
        {
          name: "Median",
          data: [],
        },
        {
          name: "1sd",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "3sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else {
    return null
  }
  const title = options.title.text
  options.title.text = ""
  return (
    <PageBody>
      <div className="nutri-title mb-3">{title}</div>
      <Row>
        <Col md={12} xs={12} style={{ color: "#000 !important" }}>
          {
            width == true ?
              <div style={{ maxWidth: "1000px" }}>
                <ReactApexChart
                  options={options}
                  series={series}
                  type="line"
                  height={530}
                />
              </div> :
              <ReactApexChart
                options={options}
                series={series}
                type="line"
                height={530}
              />
          }
        </Col>
      </Row>
    </PageBody>
  );
};

export const CustomerChartWeight = (props: any) => {
  const { diff, patient, customer, type, width } = props;
  console.log(type, "thaiiof");

  const curentPointLockup: any = []
  const birthDate = moment(customer?.birthday);
  customer?.examinations?.map((item: any) => {
    const month = moment(item?.day).diff(birthDate, "months");
    if (Number(item?.height)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      height: Number(item?.height)
    }
    if (Number(item?.weight)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      weight: Number(item?.weight),
    }
    if (Number(item?.height) && Number(item?.weight)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      bmi: getBmi(item),
    }
  })
  const options: any = cloneDeep(OPTIONS)
  options.xaxis.title.text = 'Tuổi'
  options.yaxis.title.text = 'Cân nặng (kg)'
  let series = [];
  if (diff?.months <= 60 && patient?.sex === "male") {
    options.colors = [COLORS?.["2sd"], COLORS?.["m"], COLORS?.["2sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ cân nặng so với tuổi của bé trai từ 0 đến 60 tháng"

    options.xaxis.title.text = "Tháng"
    options.xaxis.categories = LESS_THAN_60_MONTHS?.male?.weight?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.month];
        return prev;
      },
      []
    );
    let i: number = 0
    series = LESS_THAN_60_MONTHS?.male?.weight?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-2sd"])
        prev[1].data.push(item["m"])
        prev[2].data.push(item["2sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[3].data.push(curentPointLockup[Number(item?.month)]?.weight)
          if (curentPointLockup[Number(item?.month)]?.weight) {
            i++;
            options.annotations.points.push({
              x: Number(item?.month),
              y: curentPointLockup[Number(item?.month)]?.weight,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[3].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "m",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.months <= 60 && patient?.sex === "female") {
    options.colors = [COLORS?.["2sd"], COLORS?.["m"], COLORS?.["2sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ cân nặng so với tuổi của bé gái từ 0 đến 60 tháng"
    options.xaxis.title.text = "Tháng"
    options.xaxis.categories = LESS_THAN_60_MONTHS?.female?.weight?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.month];
        return prev;
      },
      []
    );
    let i: number = 0
    series = LESS_THAN_60_MONTHS?.female?.weight?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-2sd"])
        prev[1].data.push(item["m"])
        prev[2].data.push(item["2sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[3].data.push(curentPointLockup[Number(item?.month)]?.weight)
          if (curentPointLockup[Number(item?.month)]?.weight) {
            i++;
            options.annotations.points.push({
              x: Number(item?.month),
              y: curentPointLockup[Number(item?.month)]?.weight,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[3].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "m",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.years <= 10 && patient?.sex === "male") {
    options.colors = [COLORS?.["3sd"], COLORS?.["2sd"], COLORS?.["1sd"], COLORS?.["m"], COLORS?.["1sd"], COLORS?.["2sd"], COLORS?.["3sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ cân nặng so với tuổi của bé trai từ 5 đến 10 tuổi"
    options.xaxis.title.text = "Năm : Tháng"
    options.xaxis.categories = WEIGHT_MALE_5_10?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.name];
        return prev;
      },
      []
    );
    let i: number = 0
    series = WEIGHT_MALE_5_10?.reduce(
      (prev: any, item: any, index: any) => {
        prev[0].data.push(item["-3sd"])
        prev[1].data.push(item["-2sd"])
        prev[2].data.push(item["-1sd"])
        prev[3].data.push(item["Median"])
        prev[4].data.push(item["1sd"])
        prev[5].data.push(item["2sd"])
        prev[6].data.push(item["3sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[7].data.push(curentPointLockup[Number(item?.month)]?.weight)
          if (curentPointLockup[Number(item?.month)]?.weight) {
            i++;
            options.annotations.points.push({
              x: item?.name,
              y: curentPointLockup[Number(item?.month)]?.weight,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[7].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-3sd",
          data: [],
        },
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "-1sd",
          data: [],
        },
        {
          name: "Median",
          data: [],
        },
        {
          name: "1sd",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "3sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.years <= 10 && patient?.sex === "female") {
    options.colors = [COLORS?.["3sd"], COLORS?.["2sd"], COLORS?.["1sd"], COLORS?.["m"], COLORS?.["1sd"], COLORS?.["2sd"], COLORS?.["3sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ cân nặng so với tuổi của bé gái từ 5 đến 10 tuổi"
    options.xaxis.title.text = "Năm : Tháng"
    options.xaxis.categories = WEIGHT_FEMALE_5_10?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.name];
        return prev;
      },
      []
    );
    let i: number = 0
    series = WEIGHT_FEMALE_5_10?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-3sd"])
        prev[1].data.push(item["-2sd"])
        prev[2].data.push(item["-1sd"])
        prev[3].data.push(item["Median"])
        prev[4].data.push(item["1sd"])
        prev[5].data.push(item["2sd"])
        prev[6].data.push(item["3sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[7].data.push(curentPointLockup[Number(item?.month)]?.weight)
          if (curentPointLockup[Number(item?.month)]?.weight) {
            i++;
            options.annotations.points.push({
              x: item?.name,
              y: curentPointLockup[Number(item?.month)]?.weight,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[7].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-3sd",
          data: [],
        },
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "-1sd",
          data: [],
        },
        {
          name: "Median",
          data: [],
        },
        {
          name: "1sd",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "3sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else {
    return null
  }
  const title = options.title.text
  options.title.text = ""
  return (
    <PageBody>
      <div className="nutri-title mb-3">{title}</div>
      <Row>
        <Col md={12} xs={12} style={{ color: "#000 !important" }}>
          {
            width == true ?
              <div style={{ maxWidth: "1000px" }}>
                <ReactApexChart
                  options={options}
                  series={series}
                  type="line"
                  height={530}
                />
              </div> :
              <ReactApexChart
                options={options}
                series={series}
                type="line"
                height={530}
              />
          }
        </Col>
      </Row>
    </PageBody>
  );
};

export const CustomerChartBMI = (props: any) => {
  const { diff, patient, customer, type, width } = props;
  console.log(type, "thaiiof");

  if (diff?.months <= 60) return null
  const curentPointLockup: any = []
  const birthDate = moment(customer?.birthday);
  customer?.examinations?.map((item: any) => {
    const month = moment(item?.day).diff(birthDate, "months");
    if (Number(item?.height)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      height: Number(item?.height)
    }
    if (Number(item?.weight)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      weight: Number(item?.weight),
    }
    if (Number(item?.height) && Number(item?.weight)) curentPointLockup[Number(month)] = {
      ...curentPointLockup[Number(month)],
      bmi: getBmi(item),
    }
  })
  const options: any = cloneDeep(OPTIONS)
  options.xaxis.title.text = 'Tuổi'
  options.yaxis.title.text = 'BMI'
  let series = [];
  if (diff?.years <= 19 && patient?.sex === "male") {
    options.colors = [COLORS?.["3sd"], COLORS?.["2sd"], COLORS?.["1sd"], COLORS?.["m"], COLORS?.["1sd"], COLORS?.["2sd"], COLORS?.["3sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ BMI so với tuổi của bé trai từ 5 đến 19 tuổi"
    options.xaxis.title.text = "Tháng"
    options.xaxis.categories = BMI_MALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.name];
        return prev;
      },
      []
    );
    let i: number = 0
    series = BMI_MALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-3sd"])
        prev[1].data.push(item["-2sd"])
        prev[2].data.push(item["-1sd"])
        prev[3].data.push(item["Median"])
        prev[4].data.push(item["1sd"])
        prev[5].data.push(item["2sd"])
        prev[6].data.push(item["3sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[7].data.push(curentPointLockup[Number(item?.month)]?.bmi)
          if (curentPointLockup[Number(item?.month)]?.bmi) {
            i++;
            options.annotations.points.push({
              x: item?.name,
              y: curentPointLockup[Number(item?.month)]?.bmi,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[7].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-3sd",
          data: [],
        },
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "-1sd",
          data: [],
        },
        {
          name: "Median",
          data: [],
        },
        {
          name: "1sd",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "3sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else if (diff?.years <= 19 && patient?.sex === "female") {
    options.colors = [COLORS?.["3sd"], COLORS?.["2sd"], COLORS?.["1sd"], COLORS?.["m"], COLORS?.["1sd"], COLORS?.["2sd"], COLORS?.["3sd"], COLORS?.["p"]];
    options.title.text = "Biểu đồ BMI so với tuổi của bé gái từ 5 đến 19 tuổi"
    options.xaxis.title.text = "Năm : Tháng"
    options.xaxis.categories = BMI_FEMALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev = [...prev, item?.name];
        return prev;
      },
      []
    );
    let i: number = 0
    series = BMI_FEMALE_5_19?.reduce(
      (prev: any, item: any) => {
        prev[0].data.push(item["-3sd"])
        prev[1].data.push(item["-2sd"])
        prev[2].data.push(item["-1sd"])
        prev[3].data.push(item["Median"])
        prev[4].data.push(item["1sd"])
        prev[5].data.push(item["2sd"])
        prev[6].data.push(item["3sd"])
        if (curentPointLockup[Number(item?.month)]) {
          prev[7].data.push(curentPointLockup[Number(item?.month)]?.bmi)
          if (curentPointLockup[Number(item?.month)]?.bmi) {
            i++;
            options.annotations.points.push({
              x: item?.name,
              y: curentPointLockup[Number(item?.month)]?.bmi,
              marker: {
                size: 4,
                fillColor: COLORS?.["p"],
                strokeColor: COLORS?.["p"],
                radius: 2,
                cssClass: "apexcharts-custom-class"
              },
              label: {
                borderColor: COLORS?.["p"],
                width: 0,
                borderRadius: "50%",
                text: i,
                textAnchor: "1",
                offsetX: -3,
                offsetY: 9,
                style: {
                  width: 5,
                  background: COLORS?.["p"],
                  color: COLORS?.["p"],
                  fontSize: '5px',
                  fontWeight: 50,
                  fontFamily: undefined,
                  cssClass: 'apexcharts-point-annotation-label',
                },
              },
            })
          }
        } else {
          prev[7].data.push(null)
        }
        return prev;
      },
      [
        {
          name: "-3sd",
          data: [],
        },
        {
          name: "-2sd",
          data: [],
        },
        {
          name: "-1sd",
          data: [],
        },
        {
          name: "Median",
          data: [],
        },
        {
          name: "1sd",
          data: [],
        },
        {
          name: "2sd",
          data: [],
        },
        {
          name: "3sd",
          data: [],
        },
        {
          name: "Point",
          data: [],
        },
      ]
    );
  } else {
    return null
  }
  const title = options.title.text
  options.title.text = ""
  return (
    <PageBody>
      <div className="nutri-title mb-3">{title}</div>
      <Row>
        <Col md={12} xs={12} style={{ color: "#000 !important" }}>
          {
            width == true ?
              <div style={{width : "1000px"}}>
                <ReactApexChart
                  options={options}
                  series={series}
                  type="line"
                  height={530}
                />
              </div> :
              <ReactApexChart
                options={options}
                series={series}
                type="line"
                height={530}
              />
          }

        </Col>
      </Row>
    </PageBody>
  );
};

const CustomerChart = (props: any) => {
  const componentRef = useRef(null);
  const [width, setWidth] = useState<any>()
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: true,
  });
  const handlePrintChart = () => {
    setWidth(true)
    setTimeout(() => {
      handlePrint()
    }, 2000)
  }
  const router = useHistory()
  const now = moment();
  if (!props?.birthday) return null;
  const name = props?.name;
  const sex = props?.sex;
  const birthDate = moment(props?.birthday);
  const years = now.diff(birthDate, "year");
  const months = now.diff(birthDate, "months");
  const days = now.diff(birthDate, "days");
  const patient: any = {
    firstname: "",
    lastname: name,
    sex: sex,
    birthdate: moment(props?.birthday).format("YYYY-MM-DD"),
    measures: [
      {
        date: moment().format("YYYY-MM-DD"),
        height: 51,
        weight: 7,
      },
    ],
  };


  return (
    <>
      {/* <CustomerChartHeight1
        patient={patient}
        customer={props}
        diff={{
          years: years,
          months: months,
          days: days,
        }}
      /> */}
      <CustomerChartHeight
        patient={patient}
        customer={props}
        diff={{
          years: years,
          months: months,
          days: days,
        }}
      />
      <CustomerChartWeight
        patient={patient}
        customer={props}
        diff={{
          years: years,
          months: months,
          days: days,
        }}
      />
      <CustomerChartBMI
        patient={patient}
        customer={props}
        diff={{
          years: years,
          months: months,
          days: days,
        }}
      />
      {
        <div
          style={{overflow : "hidden" , height : "0"}}
        >
          <CustomerChartPrint
            width={width}
            componentRef={componentRef}
            print={"print"}
            patient={patient}
            customer={props}
            years={years}
            months={months}
            days={days}
            data={props}
            user={props?.user}
            className="horizontal-print"
          />
        </div>
      }
      <div className="bottom-action">
        <Button color="success" className='success-bland ml-2' onClick={() => router?.goBack()}>Đóng</Button>
        {
          <Button color="success" className='ml-2' onClick={() => handlePrintChart()}>In</Button>
        }
      </div>
    </>
  );
};

export default CustomerChart;
